import React, { useState, useRef, useEffect, useCallback } from 'react';
import SubMenuBar from './SubMenuBar';

import './Gracenotes.css';
import { useNavigate } from 'react-router-dom';
import ChanterFingeringAnimator from './ChanterFingeringAnimator';
import { 
  DEFAULT_GAIN,
  GLOBAL_GAIN_KEY
} from '../utils/constants';
import { getItem } from '../utils/storage';
import VexFlowLoader from '../utils/vexflowLoader';
import StaffRenderer from './StaffRenderer';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

function Gracenotes() {
  const [activeNote, setActiveNote] = useState(null);
  const [activeMelodyNote, setActiveMelodyNote] = useState(null);
  const navigate = useNavigate();
  const [showFingering, setShowFingering] = useState(true);
  const staffContainerRef = useRef(null);
  const rendererRef = useRef(null);
  const contextRef = useRef(null);
  const staveRef = useRef(null);
  const audioContextRef = useRef(null);
  const isMobile = useIsMobile();

  // Define bagpipe notes with VexFlow mapping
  const noteMapping = {
    'Low G': { key: 'g', octave: '/4' },
    'Low A': { key: 'a', octave: '/4' },
    'B': { key: 'b', octave: '/4' },
    'C': { key: 'c', octave: '/5' },
    'D': { key: 'd', octave: '/5' },
    'E': { key: 'e', octave: '/5' },
    'F': { key: 'f', octave: '/5' },
    'High G': { key: 'g', octave: '/5' },
    'High A': { key: 'a', octave: '/5' }
  };

  const gracenoteNotes = [
    { name: 'High A', frequency: 960 },
    { name: 'High G', frequency: 840 },
    { name: 'F', frequency: 800 },
    { name: 'E', frequency: 720 },
    { name: 'D', frequency: 640 },
    { name: 'C', frequency: 600 },
    { name: 'B', frequency: 540 },
    { name: 'Low A', frequency: 480 },
    { name: 'Low G', frequency: 426 },
  ];

  // Helper to get note order for comparison
  const noteOrder = [
    'Low G',
    'Low A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'High G',
    'High A'
  ];

  const handleNoteClick = note => {
    // Prevent selecting a grace note that is not higher than the melody note
    if (activeMelodyNote) {
      const graceIdx = noteOrder.indexOf(note.name);
      const melodyIdx = noteOrder.indexOf(activeMelodyNote);
      if (graceIdx <= melodyIdx) return;
    }
    setActiveNote(note.name === activeNote ? null : note.name);
  };

  const handleMelodyNoteClick = note => {
    // Prevent selecting a melody note that is not lower than the grace note
    if (activeNote) {
      const melodyIdx = noteOrder.indexOf(note.name);
      const graceIdx = noteOrder.indexOf(activeNote);
      if (melodyIdx >= graceIdx) return;
    }
    setActiveMelodyNote(note.name === activeMelodyNote ? null : note.name);
  };

  const toggleFingering = (show) => {
    setShowFingering(show);
  };

  // Define menus for SubMenuBar
  const menus = {
    display: {
      label: 'Display',
      items: ['Show Fingering', 'Hide Fingering'],
      selected: showFingering ? 'Show Fingering' : 'Hide Fingering',
      onSelect: (item) => {
        toggleFingering(item === 'Show Fingering');
      }
    },
  };

  // Initialize VexFlow staff - this is now done via the StaffRenderer
  useEffect(() => {
    // No explicit cleanup for DOM nodes - React will handle this
    return () => {
      // Just clean up our refs
      rendererRef.current = null;
      contextRef.current = null;
      staveRef.current = null;
    };
  }, []);

  // Update staff when melody note or grace note changes - now done through renderStaff function
  const renderStaff = useCallback((vexflow, containerElement) => {
    console.log('renderStaff called with vexflow:', !!vexflow);
    
    if (!containerElement) {
      console.error('No container element provided');
      return;
    }

    // Safely clear previous content
    try {
      console.log('Clearing staff container');
      // Clear all SVG elements in a React-safe way
      containerElement.innerHTML = '';
    } catch (error) {
      console.error('Error clearing staff container:', error);
      return; // Exit if we can't clear the container
    }

    try {
      console.log('Setting up VexFlow renderer');
      // Extract required VexFlow components
      const { Renderer, Stave, StaveNote, Voice, Formatter, GraceNote, GraceNoteGroup } = vexflow;

      // Responsive width for staff
      const containerWidth = containerElement.getBoundingClientRect().width;
      console.log('Container width:', containerWidth);
      const svgWidth = Math.max(320, containerWidth - 20); // 10px padding on each side
      const svgHeight = 180; // Taller height to make notes more visible
      
      console.log('Creating renderer with dimensions:', svgWidth, svgHeight);
      const renderer = new Renderer(containerElement, Renderer.Backends.SVG);
      rendererRef.current = renderer;
      renderer.resize(svgWidth, svgHeight);

      const context = renderer.getContext();
      contextRef.current = context;
      context.setFont('Arial', 10);

      // Position the stave in the middle of the container with proper spacing
      console.log('Creating stave');
      const stave = new Stave(10, 40, svgWidth - 20); // Starting at y=40 to center it
      staveRef.current = stave;
      stave.addClef('treble');
      stave.setContext(context).draw();

      if (activeMelodyNote) {
        console.log('Drawing notes for melody:', activeMelodyNote);
        const noteInfo = noteMapping[activeMelodyNote];
        if (noteInfo) {
          // Create two sequential notes that total 4 beats
          const notes = [
            new StaveNote({
              clef: 'treble',
              keys: [noteInfo.key + noteInfo.octave],
              duration: '2'  // Half note (2 beats)
            }),
            new StaveNote({
              clef: 'treble',
              keys: [noteInfo.key + noteInfo.octave],
              duration: '2'  // Half note (2 beats)
            })
          ];

          // If a grace note is selected, insert it before the second melody note
          if (activeNote && noteMapping[activeNote]) {
            console.log('Adding grace note:', activeNote);
            const graceInfo = noteMapping[activeNote];
            const graceNote = new GraceNote({
              keys: [graceInfo.key + graceInfo.octave],
              duration: '8',
              slash: true,
              clef: 'treble'
            });
            const graceGroup = new GraceNoteGroup([graceNote], false); // false = no slur
            notes[1].addModifier(graceGroup, 0);
          }

          // Create a voice with proper time signature (4/4)
          const voice = new Voice({
            num_beats: 4,    // Four beats per measure
            beat_value: 4    // Quarter note gets one beat
          });
          voice.addTickables(notes);

          // Format and draw with better spacing
          console.log('Formatting and drawing voice');
          const formatter = new Formatter()
            .joinVoices([voice])
            .format([voice], svgWidth - 100, {align_rests: true});
          
          // Set note styling for better visibility
          notes.forEach(note => {
            note.setStyle({
              fillStyle: 'black',
              strokeStyle: 'black',
              strokeWidth: 2
            });
          });
          
          // Draw the voice
          voice.draw(context, stave);
          console.log('Staff rendered successfully with enhanced styling');
        }
      } else {
        console.log('No melody note selected, drawing empty staff');
      }
    } catch (error) {
      console.error('Error rendering staff:', error);
    }
  }, [activeMelodyNote, activeNote, noteMapping]);

  // Audio playback logic
  const playNote = (frequency, duration = 0.5) => {
    if (!frequency) return;
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
    const gainValue = parseFloat(getItem(GLOBAL_GAIN_KEY, DEFAULT_GAIN));
    gainNode.gain.setValueAtTime(gainValue, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + duration);
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  };

  const getNoteFrequency = (noteName) => {
    const note = gracenoteNotes.find(n => n.name === noteName);
    return note ? note.frequency : null;
  };

  const handlePlay = async () => {
    if (!activeMelodyNote) return;
    const melodyFreq = getNoteFrequency(activeMelodyNote);
    // Play first melody note
    playNote(melodyFreq, 0.5);
    await new Promise(res => setTimeout(res, 500));
    // Play grace note if present
    if (activeNote) {
      const graceFreq = getNoteFrequency(activeNote);
      playNote(graceFreq, 0.2);
      await new Promise(res => setTimeout(res, 200));
    }
    // Play second melody note
    playNote(melodyFreq, 0.5);
  };

  return (
    <div className="gracenotes-container">
      <SubMenuBar menus={menus} />
      <div className="gracenotes-content">
        {isMobile ? (
          <>
            {/* --- MOBILE: Top row with three columns --- */}
            <div className="gracenotes-mobile-top-row">
              <div className="gracenotes-column gracenotes-column--grace">
                <h2>Grace</h2>
                <div className="gracenotes-controls">
                  <div className="gracenotes-note-buttons">
                    {gracenoteNotes.map(note => (
                      <button
                        key={note.name}
                        className={`gracenotes-note-button${activeNote === note.name ? ' active' : ''}`}
                        onClick={() => handleNoteClick(note)}
                        disabled={
                          activeMelodyNote && noteOrder.indexOf(note.name) <= noteOrder.indexOf(activeMelodyNote)
                        }
                      >
                        {note.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="gracenotes-column gracenotes-column--melody">
                <h2>Melody</h2>
                <div className="melody-controls">
                  <div className="gracenotes-note-buttons">
                    {gracenoteNotes.map(note => (
                      <button
                        key={note.name}
                        className={`gracenotes-note-button${activeMelodyNote === note.name ? ' active' : ''}`}
                        onClick={() => handleMelodyNoteClick(note)}
                        disabled={
                          activeNote && noteOrder.indexOf(note.name) >= noteOrder.indexOf(activeNote)
                        }
                      >
                        {note.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* SVG column always present if showFingering */}
              {showFingering && (
                <div className="gracenotes-column gracenotes-column--svg">
                  <h2 style={{visibility: 'hidden', height: 0, margin: 0, padding: 0}}>Fingering</h2>
                  <div className="gracenotes-chanter-svg-wrapper">
                    <ChanterFingeringAnimator
                      melodyNote={activeMelodyNote}
                      graceNote={activeNote}
                      width={80}
                      height={180}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* --- MOBILE: Staff and play button below --- */}
            <div className="gracenotes-mobile-bottom">
              <div className="gracenotes-staff-play-wrapper">
                <button className="gracenotes-play-button" onClick={handlePlay}>Play</button>
                <StaffRenderer 
                  renderStaff={renderStaff}
                  activeNote={activeNote}
                  activeMelodyNote={activeMelodyNote}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* DESKTOP/TABLET: Three columns: grace, melody, staff */}
            <div className="gracenotes-column">
              <h2>Grace</h2>
              <div className="gracenotes-controls">
                <div className="gracenotes-note-buttons">
                  {gracenoteNotes.map(note => (
                    <button
                      key={note.name}
                      className={`gracenotes-note-button${activeNote === note.name ? ' active' : ''}`}
                      onClick={() => handleNoteClick(note)}
                      disabled={
                        activeMelodyNote && noteOrder.indexOf(note.name) <= noteOrder.indexOf(activeMelodyNote)
                      }
                    >
                      {note.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="gracenotes-column">
              <h2>Melody</h2>
              <div className="melody-controls">
                <div className="gracenotes-note-buttons">
                  {gracenoteNotes.map(note => (
                    <button
                      key={note.name}
                      className={`gracenotes-note-button${activeMelodyNote === note.name ? ' active' : ''}`}
                      onClick={() => handleMelodyNoteClick(note)}
                      disabled={
                        activeNote && noteOrder.indexOf(note.name) >= noteOrder.indexOf(activeNote)
                      }
                    >
                      {note.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="gracenotes-column">
              <h2>Staff</h2>
              <div className="gracenotes-staff-play-wrapper">
                <StaffRenderer 
                  renderStaff={renderStaff}
                  activeNote={activeNote}
                  activeMelodyNote={activeMelodyNote}
                />
                <button className="gracenotes-play-button" onClick={handlePlay}>Play</button>
                {showFingering && (
                  <div className="gracenotes-chanter-svg-wrapper">
                    <ChanterFingeringAnimator
                      melodyNote={activeMelodyNote}
                      graceNote={activeNote}
                      width={120}
                      height={300}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Gracenotes; 